/**
 * Telemetry device registry — `/api/device` CRUD.
 */
import { DEFAULT_PAGE_SIZE } from '@/config/fleetConstants'
import { getAuthHeaders, valtFetch, VALT_API_BASE } from '@/utils/api'

export interface ApiDevice {
  id: number
  code?: string
  name: string
  model: string
  serial: string
  telematics_provider?: string | null
  phone_number?: string
  enabled: boolean
  shared?: boolean
  shared_public?: boolean
  vehicle?: { id: number; name?: string; plate?: string } | null
  vehicle_id?: number | null
  user?: { id: number; name?: string } | null
  created_at?: string
  updated_at?: string
}

export interface ApiTelematicsProvider {
  id: number
  code: string
  name: string
  enabled: boolean
}

export interface CreateApiDeviceData {
  code?: string
  name: string
  model: string
  serial: string
  telematics_provider?: string | null
  enabled?: boolean
  vehicle_id?: number | null
  phone_number?: string
  password?: string
  shared?: boolean
  shared_public?: boolean
}

export interface CreateApiTelematicsProviderData {
  code: string
  name: string
  enabled?: boolean
}

export interface DeviceListFilters {
  telematics_provider?: string
  /** Alias accepted by the API (`provider` or `telematics_provider`). */
  provider?: string
  vehicle_assigned?: boolean
  page?: number
  per_page?: number
  /** Server search across serial, name, model, code (`search` / `q`). */
  search?: string
}

export interface DeviceListPagination {
  current_page: number
  per_page: number
  total: number
  last_page: number
  from?: number
  to?: number
}

export interface DeviceListResponse {
  data: ApiDevice[]
  pagination: DeviceListPagination
}

export class DeviceApiService {
  private static headers(): Record<string, string> {
    return getAuthHeaders()
  }

  private static url(path = '', extraParams?: URLSearchParams): string {
    const qs = (extraParams ?? new URLSearchParams()).toString()
    return `${VALT_API_BASE}/device${path}${qs ? `?${qs}` : ''}`
  }

  private static parseProviderList(json: unknown): ApiTelematicsProvider[] {
    if (Array.isArray(json)) return json as ApiTelematicsProvider[]
    if (json && typeof json === 'object' && Array.isArray((json as { data?: unknown }).data)) {
      return (json as { data: ApiTelematicsProvider[] }).data
    }
    return []
  }

  private static parseList(json: unknown): ApiDevice[] {
    if (Array.isArray(json)) return json as ApiDevice[]
    if (json && typeof json === 'object') {
      const obj = json as Record<string, unknown>
      if (Array.isArray(obj.data)) return obj.data as ApiDevice[]
      if (Array.isArray(obj.devices)) return obj.devices as ApiDevice[]
    }
    return []
  }

  private static parsePagination(
    json: unknown,
    filters?: DeviceListFilters,
    rowCount = 0,
  ): DeviceListPagination {
    const perPage = filters?.per_page ?? DEFAULT_PAGE_SIZE
    const page = filters?.page ?? 1
    if (json && typeof json === 'object') {
      const obj = json as Record<string, unknown>
      const pagination = (obj.pagination ?? obj.meta ?? {}) as Record<string, number>
      const lastPage = pagination.last_page
      return {
        current_page: pagination.current_page ?? page,
        per_page: pagination.per_page ?? perPage,
        total:
          pagination.total ??
          (lastPage != null && lastPage > 1 ? lastPage * (pagination.per_page ?? perPage) : rowCount),
        last_page: lastPage ?? Math.max(1, Math.ceil(rowCount / perPage)),
        from: pagination.from,
        to: pagination.to,
      }
    }
    return {
      current_page: page,
      per_page: perPage,
      total: rowCount,
      last_page: Math.max(1, Math.ceil(rowCount / perPage)),
    }
  }

  private static parseOne(json: unknown): ApiDevice {
    if (json && typeof json === 'object' && 'data' in json) {
      return (json as { data: ApiDevice }).data
    }
    return json as ApiDevice
  }

  static async list(filters?: DeviceListFilters): Promise<DeviceListResponse> {
    const params = new URLSearchParams()
    const provider = filters?.provider ?? filters?.telematics_provider
    if (provider) {
      params.set('provider', provider)
    }
    if (filters?.vehicle_assigned != null) {
      params.set('vehicle_assigned', filters.vehicle_assigned ? 'true' : 'false')
    }
    if (filters?.page != null) {
      params.set('page', String(filters.page))
    }
    params.set('per_page', String(filters?.per_page ?? DEFAULT_PAGE_SIZE))
    const search = filters?.search?.trim()
    if (search) {
      params.set('search', search)
    }
    const response = await valtFetch(this.url('', params), {
      method: 'GET',
      headers: this.headers(),
    })
    if (!response.ok) {
      throw new Error(`Failed to fetch devices: ${response.status} ${response.statusText}`)
    }
    const json = await response.json()
    const data = this.parseList(json)
    return {
      data,
      pagination: this.parsePagination(json, filters, data.length),
    }
  }

  static async get(id: number): Promise<ApiDevice> {
    const response = await valtFetch(this.url(`/${id}`), {
      method: 'GET',
      headers: this.headers(),
    })
    if (!response.ok) {
      throw new Error(`Failed to fetch device: ${response.status} ${response.statusText}`)
    }
    const json = await response.json()
    return this.parseOne(json)
  }

  static async create(data: CreateApiDeviceData): Promise<ApiDevice> {
    const response = await valtFetch(this.url('/create'), {
      method: 'POST',
      headers: this.headers(),
      body: JSON.stringify({
        ...(data.code ? { code: data.code } : {}),
        name: data.name,
        model: data.model,
        serial: data.serial,
        telematics_provider: data.telematics_provider ?? null,
        enabled: data.enabled ?? true,
        vehicle_id: data.vehicle_id ?? null,
        phone_number: data.phone_number ?? '',
        password: data.password ?? '',
        shared: data.shared ?? false,
        shared_public: data.shared_public ?? false,
      }),
    })
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to register device: ${response.status} - ${errorText}`)
    }
    const json = await response.json()
    return this.parseOne(json)
  }

  static async update(id: number, data: Partial<CreateApiDeviceData>): Promise<ApiDevice> {
    const response = await valtFetch(this.url(`/${id}`), {
      method: 'PATCH',
      headers: this.headers(),
      body: JSON.stringify({
        ...(data.code ? { code: data.code } : {}),
        name: data.name,
        model: data.model,
        serial: data.serial,
        telematics_provider: data.telematics_provider ?? null,
        enabled: data.enabled ?? true,
        vehicle_id: data.vehicle_id ?? null,
        phone_number: data.phone_number ?? '',
        password: data.password ?? '',
        shared: data.shared ?? false,
        shared_public: data.shared_public ?? false,
      }),
    })
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to update device: ${response.status} - ${errorText}`)
    }
    const json = await response.json()
    return this.parseOne(json)
  }

  static async assignVehicle(deviceId: number, vehicleId: number | null): Promise<ApiDevice> {
    const device = await this.get(deviceId)
    return this.update(deviceId, {
      ...(device.code ? { code: device.code } : {}),
      name: device.name,
      model: device.model,
      serial: device.serial,
      telematics_provider: device.telematics_provider ?? null,
      enabled: device.enabled,
      vehicle_id: vehicleId,
    })
  }

  static async findAssignedToVehicle(vehicleId: number): Promise<ApiDevice | null> {
    const { data: devices } = await this.list({ vehicle_assigned: true, per_page: 500 })
    return (
      devices.find((d) => d.vehicle_id === vehicleId || d.vehicle?.id === vehicleId) ?? null
    )
  }

  static async remove(id: number): Promise<void> {
    const response = await valtFetch(this.url(`/${id}`), {
      method: 'DELETE',
      headers: this.headers(),
    })
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to delete device: ${response.status} - ${errorText}`)
    }
  }

  static async listProviders(opts?: { enabled?: boolean }): Promise<ApiTelematicsProvider[]> {
    const params = new URLSearchParams()
    if (opts?.enabled != null) params.set('enabled', opts.enabled ? '1' : '0')
    const response = await valtFetch(this.url('/provider', params), {
      method: 'GET',
      headers: this.headers(),
    })
    if (!response.ok) {
      throw new Error(`Failed to fetch telematics providers: ${response.status} ${response.statusText}`)
    }
    const json = await response.json()
    return this.parseProviderList(json)
  }

  static async createProvider(data: CreateApiTelematicsProviderData): Promise<ApiTelematicsProvider> {
    const response = await valtFetch(this.url('/provider'), {
      method: 'POST',
      headers: this.headers(),
      body: JSON.stringify({
        code: data.code,
        name: data.name,
        enabled: data.enabled ?? true,
      }),
    })
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to create telematics provider: ${response.status} - ${errorText}`)
    }
    const json = await response.json()
    return this.parseOne(json) as unknown as ApiTelematicsProvider
  }

  static async updateProvider(
    id: number,
    data: Partial<CreateApiTelematicsProviderData>,
  ): Promise<ApiTelematicsProvider> {
    const response = await valtFetch(this.url(`/provider/${id}`), {
      method: 'PATCH',
      headers: this.headers(),
      body: JSON.stringify(data),
    })
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to update telematics provider: ${response.status} - ${errorText}`)
    }
    const json = await response.json()
    return this.parseOne(json) as unknown as ApiTelematicsProvider
  }

  static async removeProvider(id: number): Promise<void> {
    const response = await valtFetch(this.url(`/provider/${id}`), {
      method: 'DELETE',
      headers: this.headers(),
    })
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Failed to delete telematics provider: ${response.status} - ${errorText}`)
    }
  }
}
